// Pure helpers for the list of files selected in the "Add announcement" window (no imports on purpose).

// file name length <= 4 is too short and > 200 is too long (the same rules as in the file upload window)
const MIN_NAME_LENGTH_EXCLUSIVE = 4;
const MAX_NAME_LENGTH = 200;

// type sent by the browser for a file with unknown type
const DEFAULT_MIME_TYPE = "application/octet-stream";

export const announcementAttachmentRejectCodes = {
    nameTooShort: "name-too-short",
    nameTooLong: "name-too-long",
    unsupportedType: "unsupported-type",
    tooBig: "too-big",
};

function getFileName(file) {
    return file && typeof file.name === "string" ? file.name : "";
}

function getFileSize(file) {
    return Number(file && file.size) || 0;
}

function getExtension(name) {
    const index = name.lastIndexOf(".");
    return index < 0 ? "" : name.substring(index).toLowerCase();
}

function isSameFile(first, second) {
    return getFileName(first) === getFileName(second)
        && getFileSize(first) === getFileSize(second)
        && first.lastModified === second.lastModified;
}

// returns the reject code of the first broken rule or null when the file is valid
function getRejectCode(file, currentTotalSize, allowedMimeTypes, allowedExtensions, maxTotalSize) {
    const name = getFileName(file);

    if (name.length <= MIN_NAME_LENGTH_EXCLUSIVE) {
        return announcementAttachmentRejectCodes.nameTooShort;
    }

    if (name.length > MAX_NAME_LENGTH) {
        return announcementAttachmentRejectCodes.nameTooLong;
    }

    const type = file.type || DEFAULT_MIME_TYPE;
    const extensions = allowedExtensions.map((extension) => String(extension).toLowerCase());
    if (!allowedMimeTypes.includes(type) || !extensions.includes(getExtension(name))) {
        return announcementAttachmentRejectCodes.unsupportedType;
    }

    if (currentTotalSize + getFileSize(file) > maxTotalSize) {
        return announcementAttachmentRejectCodes.tooBig;
    }

    return null;
}

// rules = { allowedMimeTypes, allowedExtensions, maxTotalSize }
// added can be an array or an array-like object (FileList), input arrays are not modified
export function addSelectedFiles(current, added, rules) {
    const files = Array.from(current || []);
    const rejected = [];

    if (added === null || added === undefined) {
        return { files, rejected };
    }

    const allowedMimeTypes = rules.allowedMimeTypes || [];
    const allowedExtensions = rules.allowedExtensions || [];
    const maxTotalSize = rules.maxTotalSize;
    let totalSize = files.reduce((sum, file) => sum + getFileSize(file), 0);

    for (const file of Array.from(added)) {
        if (file === null || file === undefined) {
            continue;
        }

        // duplicate (already on the list or accepted earlier in this call) is skipped silently
        if (files.some((existing) => isSameFile(existing, file))) {
            continue;
        }

        const code = getRejectCode(file, totalSize, allowedMimeTypes, allowedExtensions, maxTotalSize);
        if (code !== null) {
            rejected.push({ name: getFileName(file), code });
            continue;
        }

        files.push(file);
        totalSize += getFileSize(file);
    }

    return { files, rejected };
}

export function removeSelectedFile(current, index) {
    return Array.from(current || []).filter((file, fileIndex) => fileIndex !== index);
}
